import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Loader2, CheckCircle2, Video, Calendar as CalendarIcon, ExternalLink, Globe, Upload, FileText, X } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { BOOKING } from '@/constants/testIds';
import { getTimezoneShort } from '@/lib/format';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import { getStudentSession } from '@/lib/auth';

dayjs.extend(timezone);

const TZ_OPTIONS = [
    { value: 'local', label: 'My local time' },
    { value: 'Asia/Kolkata', label: 'IST (Asia/Kolkata)' },
    { value: 'UTC', label: 'UTC' },
];

function formatInZone(iso, tz) {
    if (tz === 'local' || !tz) return dayjs(iso).format('h:mm A');
    try {
        return dayjs(iso).tz(tz).format('h:mm A');
    } catch {
        return dayjs(iso).format('h:mm A');
    }
}
function formatDateInZone(iso, tz) {
    if (tz === 'local' || !tz) return dayjs(iso).format('ddd, MMM D, YYYY');
    try {
        return dayjs(iso).tz(tz).format('ddd, MMM D, YYYY');
    } catch {
        return dayjs(iso).format('ddd, MMM D, YYYY');
    }
}
function getZoneLabel(tz) {
    if (tz === 'UTC') return 'UTC (UTC+00:00)';
    if (tz === 'Asia/Kolkata') return 'IST (Asia/Kolkata, UTC+05:30)';
    return getTimezoneShort();
}

// 3-step flow: pick slot -> confirm -> success
const STEP_SLOT = 0;
const STEP_DETAILS = 1;
const STEP_SUCCESS = 2;

const DEFAULT_DOMAINS = ['Finance', 'Operations', 'Consulting'];

export const BookingModal = ({ mentor, open, onOpenChange, onBooked, rescheduleBooking = null, domains = [] }) => {
    const isReschedule = !!rescheduleBooking;
    const [step, setStep] = useState(STEP_SLOT);
    const [duration, setDuration] = useState(30);
    const [selectedDay, setSelectedDay] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [tz, setTz] = useState('local');

    const [availability, setAvailability] = useState({ loading: false, slots: [], connected: true, message: '' });
    const [form, setForm] = useState({ topic: '' });
    const [sessionDomain, setSessionDomain] = useState('');
    const [cvFile, setCvFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [booking, setBooking] = useState(null);
    const [session, setSession] = useState(getStudentSession());

    // Domain dropdown options come from the live domain filters (fallback to defaults)
    const domainOptions = useMemo(() => {
        const list = (domains && domains.length ? domains : DEFAULT_DOMAINS);
        return Array.from(new Set(list.map((d) => (d || '').trim()).filter(Boolean)));
    }, [domains]);

    // Pull the latest student record (credits change as sessions are booked/cancelled),
    // so the modal never shows a stale credit count from the login-time cache.
    const refreshSessionStudent = async () => {
        const existing = getStudentSession();
        if (!existing) return;
        try {
            const { data } = await api.get('/student/me');
            setSession({ ...existing, student: data });
        } catch (e) {
            // Keep the cached session if the refresh fails; auth errors are handled elsewhere.
        }
    };

    // Reset on open + immediately kick off availability fetch
    useEffect(() => {
        if (open && mentor) {
            setStep(STEP_SLOT);
            setSelectedSlot(null);
            setSelectedDay(null);
            const initialDuration = rescheduleBooking?.duration_minutes || mentor.session_durations?.[0] || 30;
            setDuration(initialDuration);
            setBooking(null);
            setForm({ topic: rescheduleBooking?.topic || '' });
            // Pre-select the mentor's own domain if it's one of the available options
            const mentorDomains = (mentor.domain || '').split(',').map((d) => d.trim()).filter(Boolean);
            const optionList = (domains && domains.length ? domains : DEFAULT_DOMAINS);
            const preferred = mentorDomains.find((d) => optionList.includes(d)) || '';
            setSessionDomain(preferred);
            setCvFile(null);
            setSession(getStudentSession());
            refreshSessionStudent();
            loadAvailability(initialDuration);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mentor, rescheduleBooking?.id]);

    const loadAvailability = async (dur) => {
        setAvailability({ loading: true, slots: [], connected: true, message: '' });
        try {
            const { data } = await api.get(`/mentors/${mentor.id}/availability`, {
                params: {
                    duration_minutes: dur,
                    date_from: dayjs().toISOString(),
                    date_to: dayjs().add(14, 'day').toISOString(),
                },
            });
            setAvailability({
                loading: false,
                slots: data.slots || [],
                connected: data.calendar_connected !== false,
                message: data.message || '',
            });
            if ((data.slots || []).length > 0) {
                const firstDay = dayjs(data.slots[0].start).format('YYYY-MM-DD');
                setSelectedDay(firstDay);
            }
        } catch (e) {
            setAvailability({ loading: false, slots: [], connected: true, message: 'Failed to load slots.' });
            toast.error('Could not load availability. Please try again.');
        }
    };

    const slotsByDay = useMemo(() => {
        // Group by day in the selected timezone
        const map = {};
        for (const s of availability.slots) {
            const key = (tz === 'local' || !tz)
                ? dayjs(s.start).format('YYYY-MM-DD')
                : dayjs(s.start).tz(tz).format('YYYY-MM-DD');
            if (!map[key]) map[key] = [];
            map[key].push(s);
        }
        return map;
    }, [availability.slots, tz]);
    const days = useMemo(() => Object.keys(slotsByDay).sort(), [slotsByDay]);
    const daySlotsGrouped = useMemo(() => {
        // Group by part of day in selected timezone
        const groups = { morning: [], afternoon: [], evening: [] };
        const day = selectedDay ? slotsByDay[selectedDay] || [] : [];
        for (const s of day) {
            const hour = (tz === 'local' || !tz) ? dayjs(s.start).hour() : dayjs(s.start).tz(tz).hour();
            if (hour < 12) groups.morning.push(s);
            else if (hour < 17) groups.afternoon.push(s);
            else groups.evening.push(s);
        }
        return groups;
    }, [selectedDay, slotsByDay, tz]);

    const canNext = () => {
        if (step === STEP_SLOT) return !!selectedSlot;
        if (step === STEP_DETAILS) {
            if (!session) return false;  // must be signed in
            // New bookings require a domain + CV; reschedules do not
            if (!isReschedule && (!sessionDomain || !cvFile)) return false;
            return true;
        }
        return false;
    };

    const handleCvChange = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';  // allow re-selecting the same file
        if (!file) return;
        const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        if (!isPdf) {
            toast.error('Please upload your CV as a PDF file.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Your CV is too large. Please upload a PDF under 5 MB.');
            return;
        }
        setCvFile(file);
    };

    const handleConfirm = async () => {
        if (!selectedSlot) return;
        if (!session) {
            toast.error('Please sign in first.');
            return;
        }
        setSubmitting(true);
        try {
            if (isReschedule) {
                const { data } = await api.post(`/bookings/${rescheduleBooking.id}/reschedule`, {
                    start_iso: selectedSlot.start,
                    gradnext_date: selectedSlot.date,
                    gradnext_time_slot: selectedSlot.time_slot,
                    reason: form.topic?.trim() || undefined,
                });
                setBooking(data.booking);
                setStep(STEP_SUCCESS);
                onBooked?.(data.booking);
                toast.success('Session rescheduled. Updated invite sent to your inbox.');
            } else {
                if (!sessionDomain) { toast.error('Please choose a domain for this session.'); setSubmitting(false); return; }
                if (!cvFile) { toast.error('Please attach your CV (PDF).'); setSubmitting(false); return; }
                // 1) Upload the CV first to get a cv_id
                const fd = new FormData();
                fd.append('file', cvFile);
                const up = await api.post('/student/cv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                // 2) Create the booking with the CV + domain attached
                const { data } = await api.post('/bookings', {
                    mentor_id: mentor.id,
                    start_iso: selectedSlot.start,
                    duration_minutes: duration,
                    topic: form.topic.trim() || undefined,
                    gradnext_date: selectedSlot.date,
                    gradnext_time_slot: selectedSlot.time_slot,
                    domain: sessionDomain,
                    cv_id: up.data.cv_id,
                    cv_filename: up.data.filename,
                });
                setBooking(data.booking);
                setStep(STEP_SUCCESS);
                onBooked?.(data.booking);
                toast.success('Session booked. Check your email for the calendar invite.');
            }
        } catch (e) {
            const status = e.response?.status;
            const msg = e.response?.data?.detail || 'Booking failed. Please try again.';
            toast.error(msg);
            // 409 = slot conflict → bounce user back to slot picker with fresh availability
            if (status === 409) {
                setStep(STEP_SLOT);
                setSelectedSlot(null);
                loadAvailability(duration);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const renderStepIndicator = () => (
        <div className="mb-4 flex items-center gap-2 text-xs text-muted-foreground">
            {['Pick a slot', 'Confirm', 'Done'].map((label, i) => (
                <div key={label} className="flex items-center gap-2">
                    <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-medium tabular-nums ${
                        i <= step ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background'
                    }`}>{i + 1}</span>
                    <span className={i <= step ? 'text-foreground' : ''}>{label}</span>
                    {i < 2 && <span className="mx-1 h-px w-6 bg-border sm:w-8" />}
                </div>
            ))}
        </div>
    );

    if (!mentor) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[92vh] overflow-hidden sm:max-w-3xl p-0">
                <div className="flex max-h-[92vh] w-full min-w-0 flex-col">
                    <DialogHeader className="border-b border-border/60 px-6 pb-4 pt-6">
                        <DialogTitle className="font-display text-xl">
                            {isReschedule ? `Reschedule session with ${mentor.name}` : `Book a session with ${mentor.name}`}
                        </DialogTitle>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                            <span>{isReschedule ? 'No credit change' : '1 credit'}</span>
                            <span aria-hidden>·</span>
                            <div className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-secondary px-2 py-0.5 text-xs">
                                <Globe className="h-3 w-3 text-accent" />
                                <span className="text-primary">{getZoneLabel(tz)}</span>
                                <div className="ml-1 flex overflow-hidden rounded-full border border-border/60">
                                    {TZ_OPTIONS.map((o) => (
                                        <button
                                            key={o.value}
                                            type="button"
                                            onClick={() => setTz(o.value)}
                                            data-testid={`booking-tz-${o.value}`}
                                            className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${
                                                tz === o.value ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground'
                                            }`}
                                        >
                                            {o.value === 'local' ? 'Local' : o.value === 'Asia/Kolkata' ? 'IST' : 'UTC'}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </DialogHeader>
                    <div className="min-w-0 flex-1 overflow-auto px-6 py-5">
                        {step !== STEP_SUCCESS && renderStepIndicator()}

                        {step === STEP_SLOT && (
                            <div>
                                {availability.loading && (
                                    <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/40 p-4 text-sm text-muted-foreground">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Loading available slots…
                                    </div>
                                )}
                                {!availability.loading && availability.slots.length === 0 && (
                                    <div className="rounded-lg border border-border/60 bg-muted/40 p-6 text-sm text-muted-foreground">
                                        No slots available in the next 2 weeks. Please check back soon.
                                    </div>
                                )}
                                {!availability.loading && availability.slots.length > 0 && (
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-[220px_1fr]">
                                        <div className="flex max-h-[420px] flex-col gap-1 overflow-y-auto rounded-lg border border-border/60 bg-card p-2">
                                            {days.map((day) => (
                                                <button
                                                    key={day}
                                                    onClick={() => { setSelectedDay(day); setSelectedSlot(null); }}
                                                    className={`rounded-md px-3 py-2 text-left text-sm transition-colors ${
                                                        selectedDay === day
                                                            ? 'bg-primary text-primary-foreground'
                                                            : 'hover:bg-accent/20'
                                                    }`}
                                                >
                                                    <div className="font-medium">{dayjs(day).format('ddd, MMM D')}</div>
                                                    <div className={`text-xs ${selectedDay === day ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                                                        {slotsByDay[day].length} slot{slotsByDay[day].length !== 1 ? 's' : ''}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                        <div className="max-h-[420px] overflow-y-auto rounded-lg border border-border/60 p-3">
                                            {['morning', 'afternoon', 'evening'].map((part) => (
                                                daySlotsGrouped[part]?.length > 0 && (
                                                    <div key={part} className="mb-4">
                                                        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{part}</div>
                                                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                                                            {daySlotsGrouped[part].map((slot) => {
                                                                const active = selectedSlot?.start === slot.start;
                                                                return (
                                                                    <button
                                                                        key={slot.start}
                                                                        data-testid={BOOKING.slot_chip}
                                                                        onClick={() => setSelectedSlot(slot)}
                                                                        className={`rounded-lg border px-2 py-2 text-xs font-medium tabular-nums transition-colors ${
                                                                            active
                                                                                ? 'border-primary bg-primary text-primary-foreground'
                                                                                : 'border-border bg-card hover:bg-accent/20'
                                                                        }`}
                                                                    >
                                                                        {formatInZone(slot.start, tz)}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {step === STEP_DETAILS && (
                            <div className="space-y-4">
                                {isReschedule && (
                                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                                        <strong>Reschedule:</strong> Your original slot on {formatDateInZone(rescheduleBooking.start_iso, tz)} at {formatInZone(rescheduleBooking.start_iso, tz)} will be cancelled and replaced with the new slot below. No credit change.
                                    </div>
                                )}
                                <div className="rounded-lg border border-border/60 bg-secondary p-4 text-sm">
                                    <div className="font-medium">{isReschedule ? 'New slot' : 'Selected slot'}</div>
                                    <div className="mt-1 text-muted-foreground">
                                        {formatDateInZone(selectedSlot?.start, tz)} · {formatInZone(selectedSlot?.start, tz)} – {formatInZone(selectedSlot?.end, tz)}
                                        <span className="ml-2 text-xs">({getZoneLabel(tz)})</span>
                                    </div>
                                </div>
                                {session ? (
                                    <div className="rounded-lg border border-border/60 bg-card p-4 text-sm">
                                        <div className="text-xs uppercase tracking-wide text-muted-foreground">Booking as</div>
                                        <div className="mt-1 flex items-center justify-between gap-3">
                                            <div>
                                                <div className="font-medium text-primary">{session.student?.name}</div>
                                                <div className="text-xs text-muted-foreground">{session.student?.email} · {session.student?.college_id}</div>
                                            </div>
                                            <Badge
                                                data-testid="booking-modal-credits"
                                                className={
                                                    (session.student?.credits ?? 0) < 1
                                                        ? 'bg-destructive/10 text-destructive'
                                                        : 'bg-accent text-accent-foreground'
                                                }
                                            >
                                                Credits: {session.student?.credits ?? 0}
                                            </Badge>
                                        </div>
                                        {!isReschedule && (session.student?.credits ?? 0) < 1 && (
                                            <p
                                                data-testid="booking-modal-no-credits"
                                                className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive"
                                            >
                                                You have no credits left, so this session can’t be booked. Please contact your college admin to top up your credits.
                                            </p>
                                        )}
                                    </div>
                                ) : (
                                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                                        <strong>Please sign in first.</strong>
                                        <p className="mt-1">Close this dialog and sign in from the home screen to complete your booking.</p>
                                    </div>
                                )}
                                {!isReschedule && (
                                    <>
                                        <div>
                                            <Label>Domain <span className="text-destructive">*</span></Label>
                                            <Select value={sessionDomain} onValueChange={setSessionDomain}>
                                                <SelectTrigger className="mt-1" data-testid="booking-domain-select">
                                                    <SelectValue placeholder="Choose a domain" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {domainOptions.map((d) => (
                                                        <SelectItem key={d} value={d} data-testid={`booking-domain-${d}`}>{d}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <p className="mt-1 text-xs text-muted-foreground">Pick the area you'd like this session to focus on.</p>
                                        </div>
                                        <div>
                                            <Label>Upload your CV (PDF) <span className="text-destructive">*</span></Label>
                                            <input
                                                id="cv-file-input"
                                                type="file"
                                                accept="application/pdf,.pdf"
                                                className="hidden"
                                                onChange={handleCvChange}
                                                data-testid="booking-cv-input"
                                            />
                                            {cvFile ? (
                                                <div className="mt-1 flex items-center gap-2 rounded-lg border border-border/60 bg-card p-3 text-sm" data-testid="booking-cv-selected">
                                                    <FileText className="h-4 w-4 shrink-0 text-primary" />
                                                    <span className="min-w-0 flex-1 truncate" title={cvFile.name}>{cvFile.name}</span>
                                                    <span className="shrink-0 text-xs text-muted-foreground">{(cvFile.size / 1024 / 1024).toFixed(2)} MB</span>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7 shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                                        onClick={() => setCvFile(null)}
                                                        data-testid="booking-cv-remove"
                                                        aria-label="Remove CV"
                                                    >
                                                        <X className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            ) : (
                                                <Button
                                                    type="button"
                                                    variant="secondary"
                                                    className="mt-1 w-full"
                                                    onClick={() => document.getElementById('cv-file-input')?.click()}
                                                    data-testid="booking-cv-upload"
                                                >
                                                    <Upload className="mr-2 h-4 w-4" /> Choose PDF (max 5 MB)
                                                </Button>
                                            )}
                                            <p className="mt-1 text-xs text-muted-foreground">Your CV will be shared with the mentor ahead of the session.</p>
                                        </div>
                                    </>
                                )}
                                <div>
                                    <Label htmlFor="topic">What do you want to discuss? <span className="text-muted-foreground">(optional)</span></Label>
                                    <Textarea
                                        id="topic"
                                        placeholder="E.g. I need help preparing for FAANG system design interviews."
                                        value={form.topic}
                                        onChange={(e) => setForm((f) => ({ ...f, topic: e.target.value }))}
                                        rows={3}
                                        data-testid={BOOKING.student_topic}
                                    />
                                </div>
                            </div>
                        )}

                        {step === STEP_SUCCESS && booking && (
                            <div className="text-center">
                                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                                    <CheckCircle2 className="h-7 w-7" />
                                </div>
                                <h3 className="font-display text-xl font-semibold">
                                    {isReschedule ? 'Session rescheduled!' : 'Session booked!'}
                                </h3>
                                <p className="mt-1 text-sm text-muted-foreground">
                                    We’ve emailed you the updated Google Calendar invite. See you {dayjs(booking.start_iso).fromNow()}.
                                </p>
                                <div className="mx-auto mt-6 max-w-md rounded-lg border border-border/60 bg-card p-5 text-left text-sm">
                                    <div className="flex items-center gap-3">
                                        <div className="flex-1">
                                            <div className="text-xs uppercase tracking-wide text-muted-foreground">Mentor</div>
                                            <div className="font-medium">{booking.mentor_name}</div>
                                        </div>
                                        <Badge>{booking.duration_minutes} min</Badge>
                                    </div>
                                    <div className="mt-3">
                                        <div className="text-xs uppercase tracking-wide text-muted-foreground">When</div>
                                        <div className="font-medium">{formatDateInZone(booking.start_iso, tz)} · {formatInZone(booking.start_iso, tz)} – {formatInZone(booking.end_iso, tz)}</div>
                                        <div className="text-xs text-muted-foreground">{getZoneLabel(tz)}</div>
                                    </div>
                                </div>
                                <div className="mt-5 flex flex-col items-center justify-center gap-2 sm:flex-row">
                                    {booking.meet_link && (
                                        <a
                                            data-testid={BOOKING.success_meet}
                                            href={booking.meet_link}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                                        >
                                            <Video className="h-4 w-4" /> Join with Google Meet
                                        </a>
                                    )}
                                    {booking.google_event_link && (
                                        <a
                                            data-testid={BOOKING.success_calendar}
                                            href={booking.google_event_link}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-accent"
                                        >
                                            <CalendarIcon className="h-4 w-4" /> View calendar event
                                            <ExternalLink className="h-3.5 w-3.5" />
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter className="gap-2 border-t border-border/60 px-6 py-4">
                        {step === STEP_SUCCESS ? (
                            <Button
                                data-testid={BOOKING.success_done}
                                className="w-full sm:w-auto sm:ml-auto"
                                onClick={() => onOpenChange(false)}
                            >
                                Done
                            </Button>
                        ) : (
                            <>
                                <Button
                                    data-testid={BOOKING.close}
                                    variant="ghost"
                                    onClick={() => onOpenChange(false)}
                                >
                                    Cancel
                                </Button>
                                {step > STEP_SLOT && (
                                    <Button
                                        data-testid={BOOKING.back}
                                        variant="secondary"
                                        onClick={() => setStep((s) => s - 1)}
                                    >
                                        Back
                                    </Button>
                                )}
                                {step < STEP_DETAILS && (
                                    <Button
                                        data-testid={BOOKING.next}
                                        disabled={!canNext()}
                                        onClick={() => setStep((s) => s + 1)}
                                    >
                                        Continue
                                    </Button>
                                )}
                                {step === STEP_DETAILS && (
                                    <Button
                                        data-testid={isReschedule ? BOOKING.reschedule_confirm : BOOKING.confirm}
                                        disabled={!canNext() || submitting || (!isReschedule && (session?.student?.credits ?? 0) < 1)}
                                        onClick={handleConfirm}
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                {isReschedule ? 'Rescheduling…' : 'Booking…'}
                                            </>
                                        ) : (
                                            isReschedule ? 'Confirm reschedule' : 'Confirm & book'
                                        )}
                                    </Button>
                                )}
                            </>
                        )}
                    </DialogFooter>
                </div>
            </DialogContent>
        </Dialog>
    );
};
